import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { TaskListModel } from '@models/types'

import AssignTemplatePagePermissionsModal from '@modals/AssignTemplatePagePermissionsModal'
import CreateOrEditRelatedEntityModal from '@modals/CreateOrEditRelatedEntityModal'
import CreateOrEditTaskListItemModal from '@modals/CreateOrEditTaskListItemModal'
import CreateOrEditTrackingLinkModal from '@modals/CreateOrEditTrackingLinkModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'
import ViewAndProcessChecklistModal from '@modals/ViewAndProcessChecklistModal'
import ViewChecklistTemplateModal from '@modals/ViewChecklistTemplateModal'
import ViewRequestedChangesModal from '@modals/ViewRequestedChangesModal'
import ViewTemplatePageDetailsModal from '@modals/ViewTemplatePageDetailsModal'

import { generateUrls } from '@hooks/useTaskList'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    deleteAttachment: (payload: HandleCallbackActionParams) => void,
    deleteRelatedEntity: (payload: HandleCallbackActionParams) => void,
    deleteTrackingLink: (payload: HandleCallbackActionParams) => void,
    showAssignTemplatePagePermissionsModal: (payload: object) => void,
    showCreateOrEditRelatedEntityModal: (payload: object) => void,
    showCreateOrEditTaskListItemModal: (payload: object) => void,
    showCreateOrEditTrackingLinkModal: (payload: object) => void,
    showUploadEntityAssetsModal: (payload: object) => void,
    showViewChecklistTemplateModal: (payload: object) => void,
    showViewTemplatePageDetailsModal: (payload: object) => void,
  },
}

const callbacks = (component: string, setState) => {
  const componentCallbacks = {
    AssignTemplatePagePermissionsModal: {
      bulkAssignTemplatePagePermissions: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showAssignTemplatePagePermissionsModal: false }),
    },
    CreateOrEditRelatedEntityModal: {
      closeModal: () => setState({ showCreateOrEditRelatedEntityModal: false }),
      createRelatedEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateRelatedEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditTaskListItemModal: {
      closeModal: () => setState({ showCreateOrEditTaskListItemModal: false }),
      createTaskListItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteTaskListItem: (payload: HandleCallbackActionParams & { taskList: TaskListModel }) => {
        const { taskList } = payload
        const { editTaskListTasksUrl } = generateUrls(taskList)

        handleCallbackAction(payload)

        if (!taskList?.parent_task_id){
          window.location.hash = editTaskListTasksUrl
        }
      },
      duplicateTaskListItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      toggleTaskListItemCompletion: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTaskListItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditTrackingLinkModal: {
      closeModal: () => setState({ showCreateOrEditTrackingLinkModal: false }),
      createTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ViewAndProcessChecklistModal: {
      closeModal: () => setState({ showViewAndProcessChecklistModal: false }),
      toggleTaskListCompletion: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ViewChecklistTemplateModal: {
      closeModal: () => setState({ showViewChecklistTemplateModal: false }),
    },
    ViewRequestedChangesModal: {
      closeModal: () => setState({ showViewRequestedChangesModal: false }),
    },
    ViewTemplatePageDetailsModal: {
      closeModal: () => setState({ showViewTemplatePageDetailsModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showAssignTemplatePagePermissionsModal: false,
  showCreateOrEditRelatedEntityModal: false,
  showCreateOrEditTaskListItemModal: false,
  showCreateOrEditTrackingLinkModal: false,
  showUploadEntityAssetsModal: false,
  showViewAndProcessChecklistModal: false,
  showViewChecklistTemplateModal: false,
  showViewRequestedChangesModal: false,
  showViewTemplatePageDetailsModal: false,
}

const TaskDetail = () => {
  const { taskId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const {
    showAssignTemplatePagePermissionsModal,
    showCreateOrEditRelatedEntityModal,
    showCreateOrEditTaskListItemModal,
    showCreateOrEditTrackingLinkModal,
    showUploadEntityAssetsModal,
    showViewAndProcessChecklistModal,
    showViewChecklistTemplateModal,
    showViewRequestedChangesModal,
    showViewTemplatePageDetailsModal,
  } = state

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
    openModalKeys,
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      deleteAttachment: payload => handleCallbackAction(payload),
      deleteRelatedEntity: payload => handleCallbackAction(payload),
      deleteTrackingLink: payload => handleCallbackAction(payload),
      showAssignTemplatePagePermissionsModal: (payload: object) => {
        setModalData('AssignTemplatePagePermissionsModal', payload)
        setState({ showAssignTemplatePagePermissionsModal: true })
      },
      showCreateOrEditRelatedEntityModal: (payload: object) => {
        setModalData('CreateOrEditRelatedEntityModal', payload)
        setState({ showCreateOrEditRelatedEntityModal: true })
      },
      showCreateOrEditTaskListItemModal: (payload: object) => {
        setModalData('CreateOrEditTaskListItemModal', payload)
        setState({ showCreateOrEditTaskListItemModal: true })
      },
      showCreateOrEditTrackingLinkModal: (payload: object) => {
        setModalData('CreateOrEditTrackingLinkModal', payload)
        setState({ showCreateOrEditTrackingLinkModal: true })
      },
      showUploadEntityAssetsModal: (payload: object) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
      showViewAndProcessChecklistModal: (payload: object) => {
        setModalData('ViewAndProcessChecklistModal', payload)
        setState({ showViewAndProcessChecklistModal: true })
      },
      showViewChecklistTemplateModal: (payload: object) => {
        setModalData('ViewChecklistTemplateModal', payload)
        setState({ showViewChecklistTemplateModal: true })
      },
      showViewRequestedChangesModal: (payload: any) => {
        setModalData('ViewRequestedChangesModal', payload)
        setState({ showViewRequestedChangesModal: true })
      },
      showViewTemplatePageDetailsModal: (payload: object) => {
        setModalData('ViewTemplatePageDetailsModal', payload)
        setState({ showViewTemplatePageDetailsModal: true })
      },
    },
  }), [openModalKeys.length])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent taskListItemId={taskId} />

        <AssignTemplatePagePermissionsModal
          callbacks={callbacks('AssignTemplatePagePermissionsModal', setState)}
          showModal={showAssignTemplatePagePermissionsModal}
        />

        <CreateOrEditRelatedEntityModal
          callbacks={callbacks('CreateOrEditRelatedEntityModal', setState)}
          showModal={showCreateOrEditRelatedEntityModal}
        />

        <CreateOrEditTaskListItemModal
          callbacks={callbacks('CreateOrEditTaskListItemModal', setState)}
          showModal={showCreateOrEditTaskListItemModal}
        />

        <CreateOrEditTrackingLinkModal
          callbacks={callbacks('CreateOrEditTrackingLinkModal', setState)}
          showModal={showCreateOrEditTrackingLinkModal}
        />

        <UploadEntityAssetsModal
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />

        <ViewAndProcessChecklistModal
          callbacks={callbacks('ViewAndProcessChecklistModal', setState)}
          showModal={showViewAndProcessChecklistModal}
        />

        <ViewChecklistTemplateModal
          callbacks={callbacks('ViewChecklistTemplateModal', setState)}
          showModal={showViewChecklistTemplateModal}
        />

        <ViewRequestedChangesModal
          callbacks={callbacks('ViewRequestedChangesModal', setState)}
          showModal={showViewRequestedChangesModal}
        />

        <ViewTemplatePageDetailsModal
          callbacks={callbacks('ViewTemplatePageDetailsModal', setState)}
          requestOptions={{
            include_related_entity_owner: true,
            include_template_page_permission_ids: true,
            include_template_page_related_entities_where_subject: true,
          }}
          showModal={showViewTemplatePageDetailsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default TaskDetail
